import { Link } from "react-router-dom";
import { ArrowRight, BadgeCheck, ShieldCheck, Users, Wrench } from "lucide-react";
import { AREAS, BUSINESS } from "@/lib/site";
import { Seo } from "@/components/Seo";
import { breadcrumbJsonLd } from "@/components/Breadcrumb";
import { PageHero } from "@/components/PageHero";
import { ImageSlot } from "@/components/ImageSlot";
import { BodySections } from "@/components/service/BodySections";
import { Reveal } from "@/components/Reveal";
import { CtaBand } from "@/components/home/CtaBand";

const BREADCRUMB = [
  { label: "Home", to: "/" },
  { label: "About", to: "/about" },
];

const SECTIONS = [
  {
    heading: "How we work",
    paragraphs: [
      "DMS Home Improvements is a family-run building and renovation company based in Molesey, Surrey. For more than 20 years we have carried out extensions, loft conversions, kitchens, bathrooms, refurbishments and plastering for homeowners across South West London and Surrey. We are not a large outfit passing jobs down a chain of subcontractors — you deal with the same people from the first phone call to the final tidy-up.",
      "That continuity is the whole point of using a firm like ours. The person who quotes your job is involved in running it, so nothing gets lost in translation. We keep sites tidy, turn up when we say we will, and treat your home with respect while we are in it. Most of our work comes from word of mouth and repeat customers, which only happens when you look after people properly.",
    ],
  },
  {
    heading: "What we cover",
    paragraphs: [
      "We handle the full range of domestic building work. That means [[extensions|/extensions]] and [[loft conversions|/loft-conversions]] when you need more space, [[kitchen|/kitchens]] and [[bathroom|/bathrooms]] fitting when a room needs bringing up to date, full [[refurbishments|/refurbishments]] when a whole property needs modernising, and [[plastering|/plastering]] of every kind — from a single skimmed ceiling to rendering the outside of a house.",
      "Because our own team covers the building, the plastering and much of the finishing, you are not left coordinating half a dozen separate trades. It keeps the standard consistent and the job moving.",
    ],
  },
  {
    heading: "How we quote",
    paragraphs: [
      "Every job starts with a free site visit. We come to you, look at the work properly, talk through what you are trying to achieve and offer an honest view — including when a cheaper or simpler option would serve you better. There is no charge and no pressure.",
      "You then receive a fixed written quote that sets out what is included, so you can plan around a clear price rather than an open-ended day rate. If anything genuinely unexpected comes to light once work is underway, we talk to you about it before doing anything, not after.",
    ],
  },
];

const WHY = [
  { icon: Users, title: "Family-run and local", text: "Based in Molesey, with the same core team on your job from start to finish." },
  { icon: Wrench, title: "One team, most trades", text: "Building, plastering and finishing handled in-house for a consistent standard." },
  { icon: BadgeCheck, title: "Fixed written quotes", text: "A clear, itemised price up front — no vague figures and no surprise extras." },
  { icon: ShieldCheck, title: "20+ years' experience", text: "Two decades of extensions, conversions and refurbishments across Surrey." },
];

export default function About() {
  return (
    <div data-testid="about-page">
      <Seo
        title="About Us | DMS Home Improvements"
        description="DMS Home Improvements is a family-run building and renovation company in Molesey, Surrey, with 20+ years' experience across South West London and Surrey. Meet the team and see how we work."
        jsonLd={breadcrumbJsonLd(BREADCRUMB)}
      />
      <PageHero
        eyebrow="About Us"
        h1="About DMS Home Improvements"
        intro="A family-run building and renovation company based in Molesey, Surrey — trusted with everything from a single ceiling to a full house refurbishment across South West London and Surrey."
        breadcrumb={BREADCRUMB}
      />

      <BodySections sections={SECTIONS} />

      <section data-testid="about-team" aria-labelledby="about-team-heading" className="section section-alt">
        <div className="container-site">
          <Reveal className="max-w-[720px]">
            <h2 id="about-team-heading">Meet The Team</h2>
            <p className="sub-line mt-4 text-body">Real people you will actually see on site — not a faceless company.</p>
          </Reveal>
          <div className="mt-12 grid gap-6 md:grid-cols-2 xl:gap-8">
            <Reveal>
              <ImageSlot label="team-dave-portrait.webp" note="[PHOTO: Dave] — portrait or on-site photo of Dave, landscape or square, min 1200px wide" ratio="4:3" alt={`${BUSINESS.founderFirstName} of DMS Home Improvements`} className="rounded-card" />
              <h3 className="mt-4 text-xl">{BUSINESS.founderFirstName}</h3>
              <p className="mt-2 text-base text-body">Your main point of contact, from the free site visit and quote through to the finished job.</p>
            </Reveal>
            <Reveal delay={60}>
              <ImageSlot label="team-group-on-site.webp" note="[PHOTO: the team] — the wider team on site, landscape, min 1600px wide" ratio="4:3" alt="The DMS Home Improvements team on site" className="rounded-card" />
              <h3 className="mt-4 text-xl">The Team</h3>
              <p className="mt-2 text-base text-body">A settled team of builders and plasterers who take pride in a tidy site and a proper finish.</p>
            </Reveal>
          </div>
        </div>
      </section>

      <section data-testid="about-credentials" aria-labelledby="about-credentials-heading" className="section bg-white">
        <div className="container-site">
          <Reveal className="max-w-[720px]">
            <h2 id="about-credentials-heading">Credentials</h2>
            <p className="sub-line mt-4 text-body">The formal details, to be confirmed and published once verified.</p>
          </Reveal>
          <ul className="mt-10 grid gap-6 md:grid-cols-3">
            {[
              { label: "Accreditations", value: "[CONFIRM WITH DAVE]" },
              { label: "Insurance", value: "[CONFIRM WITH DAVE]" },
              { label: "Company number", value: "[CONFIRM WITH DAVE]" },
            ].map((c, i) => (
              <Reveal as="li" key={c.label} delay={i * 60} className="card p-6" data-testid={`credential-${i + 1}`}>
                <span className="eyebrow">{c.label}</span>
                <p className="mt-2 text-base font-semibold text-navy">{c.value}</p>
              </Reveal>
            ))}
          </ul>
        </div>
      </section>

      <section data-testid="about-why" aria-labelledby="about-why-heading" className="section section-alt">
        <div className="container-site">
          <Reveal className="max-w-[720px]">
            <h2 id="about-why-heading">Why Homeowners Choose Us</h2>
          </Reveal>
          <ul className="mt-12 grid gap-8 md:grid-cols-2 lg:grid-cols-4">
            {WHY.map((w, i) => {
              const Icon = w.icon;
              return (
                <Reveal as="li" key={w.title} delay={i * 60} data-testid={`why-point-${i + 1}`} className="border-t-2 border-brand pt-6">
                  <Icon size={26} strokeWidth={1.75} className="text-brand" aria-hidden="true" />
                  <h3 className="mt-4 text-xl">{w.title}</h3>
                  <p className="mt-3 text-base text-body">{w.text}</p>
                </Reveal>
              );
            })}
          </ul>
        </div>
      </section>

      <section data-testid="about-areas" aria-labelledby="about-areas-heading" className="section bg-white">
        <div className="container-site">
          <Reveal className="max-w-[720px]">
            <h2 id="about-areas-heading">Where We Work</h2>
            <p className="sub-line mt-4 text-body">Based in Molesey, covering the surrounding towns and villages.</p>
          </Reveal>
          <ul className="mt-10 grid gap-x-8 gap-y-1 sm:grid-cols-2 lg:grid-cols-4">
            {AREAS.map((a, i) => (
              <Reveal as="li" key={a.id} delay={i * 40}>
                <Link
                  to={a.to}
                  data-testid={`about-area-link-${a.id}`}
                  className="group flex items-center justify-between gap-3 border-b border-line py-3 text-base font-medium text-navy transition-colors duration-200 hover:text-brand"
                >
                  Builders in {a.name}
                  <ArrowRight size={18} aria-hidden="true" className="shrink-0 text-brand transition-transform duration-200 ease-out group-hover:translate-x-1" />
                </Link>
              </Reveal>
            ))}
          </ul>
        </div>
      </section>

      <CtaBand />
    </div>
  );
}
